<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { QuillEditor } from '@vueup/vue-quill';
import { ref, onMounted, reactive, watch, computed } from "vue";
import { useForm, Link } from '@inertiajs/vue3';
import Loading from 'vue-loading-overlay';
import FlatPickr from 'vue-flatpickr-component';

const props = defineProps({
    year: String,
    allHotels: Object,
    countries: Object,
    agents: Object,
    flash: Object,
    hotelData: Object,
    user: Object,
    booking: Object,
});

let isLoading = ref(false);

const configDept = ref({
    altFormat: 'J M, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
});
const configArrv = ref({
    altFormat: 'J M, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
});

// Hotel information array - initialize from booking data
const hotelIncluded = ref(props.booking.hotel_included || 'yes');
const numberOfHotels = ref(props.booking.otherbookinglocations?.length || 1);

// Initialize hotel array from booking data
const initializeHotelArray = () => {
    if (props.booking.otherbookinglocations && props.booking.otherbookinglocations.length > 0) {
        return props.booking.otherbookinglocations.map(location => {
            // Initialize itinerary for this hotel
            const itinerary = [];
            if (props.booking.itineraries) {
                // Filter itineraries for this location
                const locationItineraries = props.booking.itineraries.filter(
                    item => item.location_id === location.id
                );

                // Sort by day
                locationItineraries.sort((a, b) => a.day - b.day);

                // Add to itinerary array
                locationItineraries.forEach(item => {
                    itinerary.push({
                        day: item.day,
                        description: item.description
                    });
                });
            }

            // If no itineraries found, add a default day
            if (itinerary.length === 0) {
                itinerary.push({ day: 1, description: '' });
            }

            return {
                id: location.id,
                hotel_name: location.hotel_name,
                meal_type: location.meal_type,
                check_in: location.check_in,
                check_out: location.check_out,
                nights: location.nights,
                itinerary: itinerary
            };
        });
    } else {
        // Default empty hotel
        return [{
            hotel_name: '',
            meal_type: '',
            check_in: '',
            check_out: '',
            nights: '',
            itinerary: [{ day: 1, description: '' }]
        }];
    }
};

const hotelArray = reactive(initializeHotelArray());

// Initialize form with booking data - Fix the stay_type initialization
const BookingEditForm = useForm({
    id: props.booking.id,
    stay_type: props.booking.stay_type ? props.booking.stay_type.toString() : '0',
    booking_no: props.booking.booking_no,
    hotel_included: props.booking.hotel_included || 'yes',
    number_of_hotels: props.booking.otherbookinglocations?.length || 1,
    hotels: hotelArray,
    sight_seeing: props.booking.sight_seeing || '',
    car_used: props.booking.car_used || '',
    agent_id: props.booking.agent_id,
    arrival_date: props.booking.arrival_date,
    departure_date: props.booking.departure_date,
    arrival_flight: props.booking.arrival_flight || '',
    departure_flight: props.booking.departure_flight || '',
    payment_deadline: props.booking.payment_deadline || '',
    payment_details: props.booking.payment_details || '',
    remarks: props.booking.remarks || '',
    status: props.booking.status || 'Pending',
    resort_confirmation_number: props.booking.resort_confirmation_number || '',
    guest_name: props.booking.guest_name || '',
    number_of_adults: props.booking.number_of_adults || 1,
    number_of_children: props.booking.number_of_children || 0,
});

// Watch for stay type changes to adjust hotel options - Move this after BookingEditForm is defined
watch(() => BookingEditForm.stay_type, (newValue) => {
    if (newValue === '0') { // Single stay
        numberOfHotels.value = 1;
        updateHotelArray();
    }
});

// Watch for number of hotels changes
watch(numberOfHotels, (newValue) => {
    updateHotelArray();
});

// Update hotel array based on number of hotels
function updateHotelArray() {
    const currentLength = hotelArray.length;
    const targetLength = hotelIncluded.value === 'yes' ? parseInt(numberOfHotels.value) : 0;

    if (currentLength < targetLength) {
        // Add more hotels
        for (let i = currentLength; i < targetLength; i++) {
            hotelArray.push({
                hotel_name: '',
                meal_type: '',
                check_in: '',
                check_out: '',
                nights: '',
                itinerary: [{ day: 1, description: '' }]
            });
        }
    } else if (currentLength > targetLength) {
        // Remove excess hotels
        hotelArray.splice(targetLength, currentLength - targetLength);
    }
}

// Calculate nights from check-in and check-out dates
function calculateNights(index) {
    const checkIn = new Date(hotelArray[index].check_in);
    const checkOut = new Date(hotelArray[index].check_out);

    if (!isNaN(checkIn.getTime()) && !isNaN(checkOut.getTime())) {
        const diffTime = Math.abs(checkOut - checkIn);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        hotelArray[index].nights = diffDays;

        // Update itinerary days
        updateItineraryDays(index, diffDays);
    }
}

// Validate hotel dates to ensure proper sequencing
function validateHotelDates(hotelIndex) {
    if (hotelArray.length <= 1) return true;

    const currentHotel = hotelArray[hotelIndex];

    // If this is not the first hotel, check that check-in is after previous hotel's check-out
    if (hotelIndex > 0) {
        const prevHotel = hotelArray[hotelIndex - 1];
        if (prevHotel.check_out && currentHotel.check_in) {
            const prevCheckOut = new Date(prevHotel.check_out);
            const currentCheckIn = new Date(currentHotel.check_in);

            if (currentCheckIn < prevCheckOut) {
                alert(`Hotel ${hotelIndex + 1} check-in date must be on or after Hotel ${hotelIndex}'s check-out date.`);
                // Reset the check-in date to the previous hotel's check-out date
                currentHotel.check_in = prevHotel.check_out;
                return false;
            }
        }
    }

    // If this is not the last hotel, check that check-out is before next hotel's check-in
    if (hotelIndex < hotelArray.length - 1) {
        const nextHotel = hotelArray[hotelIndex + 1];
        if (nextHotel.check_in && currentHotel.check_out) {
            const nextCheckIn = new Date(nextHotel.check_in);
            const currentCheckOut = new Date(currentHotel.check_out);

            if (currentCheckOut > nextCheckIn) {
                alert(`Hotel ${hotelIndex + 1} check-out date must be on or before Hotel ${hotelIndex + 2}'s check-in date.`);
                // Reset the check-out date to the next hotel's check-in date
                currentHotel.check_out = nextHotel.check_in;
                return false;
            }
        }
    }

    return true;
}

// Update itinerary days based on nights
function updateItineraryDays(index, nights) {
    const currentLength = hotelArray[index].itinerary.length;

    if (currentLength < nights) {
        // Add more days
        for (let i = currentLength; i < nights; i++) {
            hotelArray[index].itinerary.push({ day: i + 1, description: '' });
        }
    } else if (currentLength > nights) {
        // Remove excess days
        hotelArray[index].itinerary.splice(nights, currentLength - nights);
    }
}

// Add itinerary day
function addItineraryDay(hotelIndex) {
    const nextDay = hotelArray[hotelIndex].itinerary.length + 1;
    hotelArray[hotelIndex].itinerary.push({ day: nextDay, description: '' });
}

// Remove itinerary day
function removeItineraryDay(hotelIndex, dayIndex) {
    if (hotelArray[hotelIndex].itinerary.length > 1) {
        hotelArray[hotelIndex].itinerary.splice(dayIndex, 1);

        // Renumber days
        hotelArray[hotelIndex].itinerary.forEach((item, idx) => {
            item.day = idx + 1;
        });
    }
}

// Computed property for overall dates
const overallDates = computed(() => {
    if (hotelIncluded.value === 'no' || hotelArray.length === 0) {
        return {
            firstCheckIn: BookingEditForm.arrival_date,
            lastCheckOut: BookingEditForm.departure_date
        };
    }

    // Find earliest check-in and latest check-out from hotels
    let firstCheckIn = null;
    let lastCheckOut = null;

    hotelArray.forEach(hotel => {
        if (hotel.check_in && (!firstCheckIn || new Date(hotel.check_in) < new Date(firstCheckIn))) {
            firstCheckIn = hotel.check_in;
        }

        if (hotel.check_out && (!lastCheckOut || new Date(hotel.check_out) > new Date(lastCheckOut))) {
            lastCheckOut = hotel.check_out;
        }
    });

    return {
        firstCheckIn,
        lastCheckOut
    };
});

// Update BookingEditForm with overall dates when they change
watch(overallDates, (newDates) => {
    if (hotelIncluded.value === 'yes' && hotelArray.length > 0) {
        BookingEditForm.arrival_date = newDates.firstCheckIn;
        BookingEditForm.departure_date = newDates.lastCheckOut;
    }
});

onMounted(() => {
    const readonlyInput = document.querySelectorAll('input[readonly]');
    readonlyInput.forEach((item) => {
        item.style.backgroundColor = '#eceef14a';
    });
});

function handleDeptDateChange() {
    configDept.value = {
        ...configDept.value,
        minDate: BookingEditForm.arrival_date
    };
}

const BookingEditFormSubmit = () => {
    // Prepare the form data
    BookingEditForm.hotels = hotelArray;

    // Set overall dates from hotels if hotels are included
    if (hotelIncluded.value === 'yes' && hotelArray.length > 0) {
        BookingEditForm.arrival_date = overallDates.value.firstCheckIn;
        BookingEditForm.departure_date = overallDates.value.lastCheckOut;
    }

    BookingEditForm.post(`/other-bookings/update/${props.booking.id}`, {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
};

</script>
<style scoped>
@import "../../../../public/ui/vendor/css/vue-quill.snow.css";
@import "vue-loading-overlay/dist/vue-loading.css";
@import "@vueform/multiselect/themes/default.css";
@import 'flatpickr/dist/themes/material_orange.css';
</style>
<template>
    <AppLayout title="Edit Booking" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="card mb-4">
                <h5 class="card-header">Edit Booking #{{ booking.booking_no }}</h5>
                <form class="card-body" @submit.prevent="BookingEditFormSubmit">
                    <h6 class="text-primary">Basic Information</h6>
                    <div class="row g-3">
                        <div class="col-md-4">
                            <label for="stay_type" class="form-label"><b>Stay Type</b></label>
                            <select class="form-select" name="stay_type" v-model="BookingEditForm.stay_type">
                                <option value="0">Single Stay</option>
                                <option value="1">Split Stay</option>
                            </select>
                        </div>
                        <div class="col-md-4">
                            <label for="booking_no_id" class="form-label"><b>Booking Number</b></label>
                            <input type="text" class="form-control" name="booking_no"
                                v-model="BookingEditForm.booking_no" readonly />
                        </div>
                        <div class="col-md-4">
                            <label for="hotel_included" class="form-label"><b>Hotels Included</b></label>
                            <select class="form-select" name="hotel_included" v-model="hotelIncluded">
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>

                        <!-- Show number of hotels selection only for split stay and when hotels are included -->
                        <div class="col-md-4" v-if="hotelIncluded === 'yes' && BookingEditForm.stay_type === '1'">
                            <label for="number_of_hotels" class="form-label"><b>Number of Hotels</b></label>
                            <select class="form-select" name="number_of_hotels" v-model="numberOfHotels">
                                <option v-for="n in 5" :key="n" :value="n">{{ n }}</option>
                            </select>
                        </div>
                    </div>

                    <!-- Hotel Information Section -->
                    <div v-if="hotelIncluded === 'yes'">
                        <hr class="my-4 mx-n4">
                        <h6 class="text-primary">Hotel Information</h6>

                        <div v-for="(hotel, hotelIndex) in hotelArray" :key="hotelIndex" class="mb-4 p-3"
                            style="border: 1px solid #eee; border-radius: 5px;">
                            <h6 class="mb-3">Hotel {{ hotelIndex + 1 }}</h6>
                            <div class="row g-3">
                                <div class="col-md-6">
                                    <label :for="'hotel_name_' + hotelIndex" class="form-label"><b>Hotel
                                            Name</b></label>
                                    <input type="text" class="form-control" :name="'hotel_name_' + hotelIndex"
                                        v-model="hotel.hotel_name" placeholder="Enter hotel name" />
                                </div>
                                <div class="col-md-6">
                                    <label :for="'meal_type_' + hotelIndex" class="form-label"><b>Meal Type</b></label>
                                    <input type="text" class="form-control" :name="'meal_type_' + hotelIndex"
                                        v-model="hotel.meal_type" placeholder="Enter meal type" />
                                </div>
                                <div class="col-md-3">
                                    <label :for="'check_in_' + hotelIndex" class="form-label"><b>Check In</b></label>
                                    <FlatPickr v-model="hotel.check_in" :config="configArrv" class="form-control"
                                        placeholder="Check in Date" :name="'check_in_' + hotelIndex"
                                        @update:model-value="() => calculateNights(hotelIndex)" />
                                </div>
                                <div class="col-md-3">
                                    <label :for="'check_out_' + hotelIndex" class="form-label"><b>Check Out</b></label>
                                    <FlatPickr v-model="hotel.check_out" :config="configDept" class="form-control"
                                        placeholder="Check out Date" :name="'check_out_' + hotelIndex"
                                        @update:model-value="() => calculateNights(hotelIndex)" />
                                </div>
                                <div class="col-md-3">
                                    <label :for="'nights_' + hotelIndex" class="form-label"><b>Nights</b></label>
                                    <input type="number" class="form-control" :name="'nights_' + hotelIndex"
                                        v-model="hotel.nights" placeholder="Calculated" readonly />
                                </div>
                            </div>

                            <!-- Itinerary Section -->
                            <div class="mt-4">
                                <h6 class="mb-3">Itinerary</h6>
                                <div v-for="(day, dayIndex) in hotel.itinerary" :key="dayIndex" class="row g-3 mb-2">
                                    <div class="col-md-2">
                                        <label :for="'day_' + hotelIndex + '_' + dayIndex" class="form-label"><b>Day {{
                                            day.day }}</b></label>
                                    </div>
                                    <div class="col-md-8">
                                        <textarea class="form-control"
                                            :name="'day_description_' + hotelIndex + '_' + dayIndex"
                                            v-model="day.description" placeholder="Enter day activities"
                                            rows="2"></textarea>
                                    </div>
                                    <div class="col-md-2">
                                        <button type="button" class="btn btn-danger btn-sm"
                                            @click="removeItineraryDay(hotelIndex, dayIndex)">
                                            <i class="tf-icons bx bx-minus"></i>
                                        </button>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-12">
                                        <button type="button" class="btn btn-primary btn-sm"
                                            @click="addItineraryDay(hotelIndex)">
                                            <i class="tf-icons bx bx-plus"></i> Add Day
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Guest Information</h6>
                    <div class="row g-3">
                        <div class="col-md-6">
                            <label for="guest_name" class="form-label"><b>Guest Name</b></label>
                            <input type="text" class="form-control" name="guest_name"
                                v-model="BookingEditForm.guest_name" placeholder="Enter guest name" required />
                            <div v-if="BookingEditForm.errors.guest_name" class="text-danger mt-1">
                                {{ BookingEditForm.errors.guest_name }}
                            </div>
                        </div>
                        <div class="col-md-3">
                            <label for="number_of_adults" class="form-label"><b>Number of Adults</b></label>
                            <input type="number" min="1" class="form-control" name="number_of_adults"
                                v-model="BookingEditForm.number_of_adults" required />
                            <div v-if="BookingEditForm.errors.number_of_adults" class="text-danger mt-1">
                                {{ BookingEditForm.errors.number_of_adults }}
                            </div>
                        </div>
                        <div class="col-md-3">
                            <label for="number_of_children" class="form-label"><b>Number of Children</b></label>
                            <input type="number" min="0" class="form-control" name="number_of_children"
                                v-model="BookingEditForm.number_of_children" />
                            <div v-if="BookingEditForm.errors.number_of_children" class="text-danger mt-1">
                                {{ BookingEditForm.errors.number_of_children }}
                            </div>
                        </div>
                    </div>

                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Additional Information</h6>
                    <div class="row g-3">
                        <div class="col-md-12">
                            <label for="sight_seeing" class="form-label"><b>Sight Seeing</b></label>
                            <textarea class="form-control" name="sight_seeing" v-model="BookingEditForm.sight_seeing"
                                placeholder="Enter sight seeing details" rows="3"></textarea>
                        </div>
                        <div class="col-md-12">
                            <label for="car_used" class="form-label"><b>Car Used</b></label>
                            <input type="text" class="form-control" name="car_used" v-model="BookingEditForm.car_used"
                                placeholder="Enter car details" />
                        </div>
                        <div class="col-md-4">
                            <label for="agent_id" class="form-label"><b>Agent</b></label>
                            <select class="form-select" name="agent_id" v-model="BookingEditForm.agent_id"
                                required="required">
                                <option value="" disabled="disabled" selected="selected">Type an agent name...</option>
                                <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                                    {{ agent.agency_name }}</option>
                            </select>
                        </div>
                    </div>

                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Travel Information</h6>
                    <div class="row g-3">
                        <!-- Only show overall check-in/check-out fields when hotels are not included -->
                        <template v-if="hotelIncluded === 'no'">
                            <div class="col-md-3">
                                <label for="arrival_date" class="form-label"><b>Overall Check in</b></label>
                                <FlatPickr v-model="BookingEditForm.arrival_date" :config="configArrv"
                                    class="form-control" placeholder="Check in Date" name="arrival_date"
                                    @change="handleDeptDateChange"
                                    :class="{ 'is-invalid': BookingEditForm.errors.arrival_date }" />
                                <div v-if="BookingEditForm.errors.arrival_date" class="invalid-feedback">
                                    <div v-text="BookingEditForm.errors.arrival_date"></div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <label for="departure_date" class="form-label"><b>Overall Check out</b></label>
                                <FlatPickr v-model="BookingEditForm.departure_date" :config="configDept"
                                    class="form-control" placeholder="Check out Date" name="departure_date"
                                    :class="{ 'is-invalid': BookingEditForm.errors.departure_date }" />
                                <div v-if="BookingEditForm.errors.departure_date" class="invalid-feedback">
                                    <div v-text="BookingEditForm.errors.departure_date"></div>
                                </div>
                            </div>
                        </template>

                        <!-- When hotels are included, show the derived overall dates as read-only -->
                        <template v-else>
                            <div class="col-md-3">
                                <label class="form-label"><b>First Check-in</b></label>
                                <input type="text" class="form-control" :value="overallDates.firstCheckIn" readonly />
                                <small class="text-muted">First hotel's check-in date</small>
                            </div>
                            <div class="col-md-3">
                                <label class="form-label"><b>Last Check-out</b></label>
                                <input type="text" class="form-control" :value="overallDates.lastCheckOut" readonly />
                                <small class="text-muted">Last hotel's check-out date</small>
                            </div>
                        </template>

                        <div class="col-md-6">
                            <label for="arrival_flight" class="form-label"><b>Arrival Flight / Info</b></label>
                            <input type="text" class="form-control" placeholder="Arrival Flight or Info"
                                name="arrival_flight" v-model="BookingEditForm.arrival_flight" />
                        </div>
                        <div class="col-md-6">
                            <label for="departure_flight" class="form-label"><b>Departure Flight / Info</b></label>
                            <input type="text" class="form-control" placeholder="Departure Flight or Info"
                                name="departure_flight" v-model="BookingEditForm.departure_flight" />
                        </div>
                    </div>

                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Payment Information</h6>
                    <div class="row g-3">
                        <div class="col-md-4">
                            <label for="payment_deadline" class="form-label"><b>Payment/Cancellation
                                    Deadline</b></label>
                            <FlatPickr v-model="BookingEditForm.payment_deadline" :config="configArrv"
                                class="form-control" placeholder="Payment Deadline" name="payment_deadline" />
                        </div>
                        <div class="col-md-8">
                            <label for="payment_details" class="form-label"><b>Payment Details</b></label>
                            <textarea class="form-control" name="payment_details"
                                v-model="BookingEditForm.payment_details" placeholder="Enter payment details"
                                rows="3"></textarea>
                        </div>
                    </div>

                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Booking Status</h6>
                    <div class="row g-3">
                        <div class="col-md-4">
                            <label for="status" class="form-label"><b>Status</b></label>
                            <select class="form-select" name="status" v-model="BookingEditForm.status">
                                <option value="Pending">Pending</option>
                                <option value="SendConfirmation">Send to Confirmation</option>
                                <option value="Confirmed">Confirmed</option>
                                <option value="Paid">Paid</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                        </div>
                        <div class="col-md-8" v-if="BookingEditForm.status === 'Confirmed'">
                            <label for="resort_confirmation_number" class="form-label"><b>Confirmation
                                    Number</b></label>
                            <input type="text" class="form-control" name="resort_confirmation_number"
                                v-model="BookingEditForm.resort_confirmation_number"
                                placeholder="Resort Confirmation Number" />
                        </div>
                    </div>

                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Other Information</h6>
                    <div class="row g-3">
                        <div class="col-md-12">
                            <label for="description" class="form-label"><b>Remarks</b></label>
                            <QuillEditor theme="snow" placeholder="    Type extra remarks"
                                v-model:content="BookingEditForm.remarks" contentType="html" toolbar="essential"
                                object="true" :searchable="true" name="remarks" />
                        </div>
                    </div>
                    <div class="modal-footer border-0 mt-3">
                        <Link type="button" class="btn btn-label-secondary"
                            :href="'/other-bookings/show/' + booking.id">
                        Cancel
                        </Link>
                        <button type="submit" class="btn btn-primary me-sm-3 me-1"
                            :disabled="BookingEditForm.processing">Update
                            Booking</button>
                    </div>
                </form>
            </div>
        </div>
    </AppLayout>
</template>